import React from 'react';

import styled from 'styled-components';
import Body2 from '@joinforma/ui/components/Body2';
import Caption from '@joinforma/ui/components/Caption';

import Currency from 'components/common/Currency';
import { PRODUCT_PAGE_ELEMENTS } from 'constants/testSelectors';
import { getPriceDifference } from 'utils/product';
import { getAmountToPoints } from 'utils/transaction';

interface PriceComparisonProps {
  new_layout?: boolean;
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceContainer = styled.div<PriceComparisonProps>`
  display: flex;
  gap: 8px;
  flex-direction: ${(props) => (props.new_layout ? 'row-reverse' : 'row')};
`;

const StrikeoutPrice = styled(Body2)<PriceComparisonProps>`
  text-decoration: line-through;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ProductPrice = styled(Body2)<PriceComparisonProps>`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${(props) => (props.new_layout ? '600' : '400')};
`;

const SavingsBoxContainer = styled.div`
  display: flex;
`;

const SavingsBox = styled(Caption)<PriceComparisonProps>`
  display: block;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${(props) => (props.new_layout ? '16px' : '100px')};
  font-weight: ${(props) => (props.new_layout ? '600' : 'initial')};
  padding: 2px 12px;
`;

const PriceComparison = ({
  company,
  currency,
  decimal_places = 2,
  display_old_price = true,
  exchange_rate = 1,
  old_price,
  new_layout,
  new_price,
  saving_bubble,
  term,
}: {
  currency: string;
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
        amount_to_points_ratio?: number;
      };
    };
  };
  decimal_places?: number;
  display_old_price?: boolean;
  exchange_rate?: number;
  old_price: number | { value: number };
  new_layout?: boolean;
  new_price: number | { value: number };
  saving_bubble: boolean;
  term: string;
}) => {
  const { display_as_amount } = company.features.stipend_config;
  const { price_difference, percent_saved } = getPriceDifference({
    msrp: old_price,
    sale_price: new_price,
  });

  const savings_box_text = new_layout
    ? `${percent_saved}% OFF`
    : `Save ${percent_saved}%`;

  return (
    <Container>
      <PriceContainer
        data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_PRICE_CONTAINER}
        new_layout={new_layout}
      >
        {price_difference > 0 && display_old_price && (
          <StrikeoutPrice
            data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_STRIKEOUT_PRICE}
            new_layout={new_layout}
          >
            {display_as_amount ? (
              <Currency
                amount={old_price as number}
                currency={currency}
                decimal_places={decimal_places}
                exchange_rate={exchange_rate}
              />
            ) : (
              getAmountToPoints({
                company,
                total: old_price,
                format: true,
              })
            )}
          </StrikeoutPrice>
        )}
        <ProductPrice
          data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_PRICE}
          new_layout={new_layout}
        >
          {display_as_amount ? (
            <Currency
              amount={new_price as number}
              currency={currency}
              decimal_places={decimal_places}
              exchange_rate={exchange_rate}
            />
          ) : (
            getAmountToPoints({
              company,
              total: new_price,
              format: true,
            })
          )}
          {term !== 'one_time' && '/mo'}
        </ProductPrice>
      </PriceContainer>
      {price_difference > 0 && saving_bubble && (
        <SavingsBoxContainer>
          <SavingsBox new_layout={new_layout}>{savings_box_text}</SavingsBox>
        </SavingsBoxContainer>
      )}
    </Container>
  );
};

export default PriceComparison;
